import Formsy from "formsy-react";
import { debounce } from "lodash";
import moment from "moment";
import * as React from "react";
import { ChangeEvent } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import {
  AddressClient,
  Channel,
  CommunicationType,
  Contact,
  ContactChannel,
  ContactType,
  Customer,
  CustomerType,
  Housing,
  NegotiatePeriod,
  ObjectClient,
  Offer,
  Order,
  OrderClient,
  PriceResponse,
  RealEstateAgentClient,
  RealEstateAgentModel,
  RealEstateChannel,
  RealEstateObject,
  RealEstateObjectConsumers,
  SoderbergPartnersClient,
} from "../api/ApiClient";
import { CopyOverride } from "../api/Config";
import { Item } from "../components/ButtonGroup";
import Checkbox from "../components/Checkbox";
import DateInput from "../components/DateInput";
import { EanInput } from "../components/EanInput";
import EmailInput from "../components/EmailInput";
import Input from "../components/Input";
import MultiselectGroup from "../components/MultiselectGroup";
import PhoneInput from "../components/PhoneInput";
import { PidInput } from "../components/PidInput";
import RadioGroup from "../components/RadioGroup";
import Spinner from "../components/Spinner";
import { parseQueryString } from "../services/QueryString";
import { FetchStatus } from "../utils/FetchStatus";
import ConfirmModal from "./ConfirmModal";
import HowItWorksModal from "./HowItWorksModal";
import NykvarnTeamSelection from "./NykvarnClubSelection";
import PriceCalculator from "./PriceCalculator";
import SavingsCalculator from "./SavingsCalculator";

interface Props {}

export enum Status {
  NOT_FOUND,
  NOT_AVAILABLE,
  DRAFT,
  CONFIRM,
  POSTING,
  ERROR,
  DONE,
}

enum OrderType {
  LEVBYTE,
  FLYTT,
}

interface State {
  valid: boolean;
  consumption: number;
  housing: Housing;
  price?: PriceResponse;
  status: Status;
  orderType: OrderType;
  hideCompanyHint: boolean;
  fetchingAddress: boolean;
  showNameInputs: boolean;
  fetchAddressNotFound: boolean;
  club: string;
  team: string;
  Agents: RealEstateAgentModel[];
  AgentStatus: FetchStatus;
  Agent?: RealEstateAgentModel;
  fetchingContact: boolean;
  fetchingObject: boolean;
  fetchObjectNotFound: boolean;
  fetchObjectAlreadyCreated: boolean;
  buyers?: RealEstateObjectConsumers[];
  sellers?: RealEstateObjectConsumers[];
  selectedBuyerId: string;
  selectedSellerId: string;
  skipFirstInput: boolean;
  object?: RealEstateObject;
  objectStatus?: string;
  objectStatusText?: string;
  customerType: CustomerType;
  sellerCustomerType: CustomerType;
}

declare global {
  interface Window {
    InitOrder: any;
    Offer: any;
    ClientCopyOverride: CopyOverride;
    isCurrent: boolean;
    seller: Customer;
  }
}

export class SignupForm extends React.Component<Props, State> {
  private confirmRef: React.RefObject<ConfirmModal> = React.createRef();
  private formRef: React.RefObject<any> = React.createRef();
  private priceCalculatorRef: React.RefObject<PriceCalculator> =
    React.createRef();
  private posting: boolean = false;
  private addressClient: AddressClient = new AddressClient();
  private objectClient = new ObjectClient();
  private initialOrder: Order = new Order();
  private offer: Offer;
  private offerPrice: number;
  private offerRebelPrice: number;
  private offerCompetitorPrice: number;

  private pidRef: React.RefObject<any> = React.createRef();
  private firstNameRef: React.RefObject<any> = React.createRef();
  private lastNameRef: React.RefObject<any> = React.createRef();
  private streetRef: React.RefObject<any> = React.createRef();
  private zipCodeRef: React.RefObject<any> = React.createRef();
  private cityRef: React.RefObject<any> = React.createRef();
  private mobileRef: React.RefObject<any> = React.createRef();
  private emailRef: React.RefObject<any> = React.createRef();

  private currentFirstNameRef: React.RefObject<any> = React.createRef();
  private currentLastNameRef: React.RefObject<any> = React.createRef();
  private startDateRef: React.RefObject<any> = React.createRef();

  private contactPidRef: React.RefObject<any> = React.createRef();
  private contactFirstNameRef: React.RefObject<any> = React.createRef();
  private contactLastNameRef: React.RefObject<any> = React.createRef();
  private contactPhoneRef: React.RefObject<any> = React.createRef();
  private contactEmailRef: React.RefObject<any> = React.createRef();
  private salesDateRef: React.RefObject<any> = React.createRef();
  private governmentApartmentNumberRef: React.RefObject<any> =
    React.createRef();
  private apartmentNumberRef: React.RefObject<any> = React.createRef();
  private objectIdRef: React.RefObject<any> = React.createRef();
  private objectAccessDateRef: React.RefObject<any> = React.createRef();
  private initConsumption: number = 1980;
  private initArea: number = 60;
  private initHousing: Housing = Housing.LAGENHET;
  private isWarrant: boolean = false;
  private initialObjectId: string = "";
  private initialAccessDate: string = "";

  private AgentRef: React.RefObject<Typeahead<RealEstateAgentModel>> =
    React.createRef();

  private controller: AbortController = null;

  constructor(props: Props) {
    super(props);

    this.parseParams();
    this.parseInitOrder();
    this.parseOffer();

    this.fetchObject = debounce(this.fetchObject, 500);
    this.state = {
      consumption: this.initConsumption,
      housing: this.initHousing,
      valid: false,
      status: Status.DRAFT,
      orderType: this.initialOrder.isCurrent
        ? OrderType.LEVBYTE
        : OrderType.FLYTT,
      hideCompanyHint: (window as any).hideCompanyHint,
      fetchingAddress: false,
      showNameInputs:
        this.isWarrant || this.initialOrder.firstName ? true : false,
      fetchAddressNotFound: false,
      club: this.initialOrder.club ?? "",
      team: this.initialOrder.team ?? "",
      Agents: [],
      AgentStatus: FetchStatus.FETCHING,
      fetchingContact: false,
      fetchingObject: false,
      fetchObjectNotFound: false,
      fetchObjectAlreadyCreated: false,
      selectedBuyerId: "-1",
      selectedSellerId: "-1",
      skipFirstInput: false,
      customerType: CustomerType.Private,
      sellerCustomerType: CustomerType.Private,
    };
  }

  // Init order from warrant
  private parseInitOrder(): void {
    //this.initialOrder.isCurrent = true;
    if (!window || !window.InitOrder) {
      return;
    }
    try {
      this.initialOrder = Order.fromJS(JSON.parse(window.InitOrder));
      this.initialOrder.area && (this.initArea = this.initialOrder.area);
      this.initialOrder.consumption &&
        (this.initConsumption = this.initialOrder.consumption);
      this.initHousing = this.initialOrder.housing
        ? Housing[this.initialOrder.housing]
        : Housing.LAGENHET;
      this.isWarrant = true;
      console.debug("Init warrent order", this.initialOrder);
    } catch (error) {
      console.error("Failed to parse init order");
    }
  }

  // Init offer
  private parseOffer(): void {
    if (!window || !window.Offer) {
      return;
    }
    try {
      this.offer = Offer.fromJS(JSON.parse(window.Offer));

      this.offerRebelPrice = Math.round(
        (this.offer.consumption * this.offer.variablePriceRebel) / 100 +
          this.offer.fixedPriceRebel * 12
      );
      this.offerCompetitorPrice = Math.round(
        (this.offer.consumption * this.offer.variablePriceCompetitor) / 100 +
          this.offer.fixedPriceCompetitor * 12
      );

      this.offerPrice = this.offerCompetitorPrice - this.offerRebelPrice;

      this.initialOrder = Order.fromJS(JSON.parse(window.Offer));
      if (this.offer.possibleStartDate) {
        this.initialOrder.startDate = this.offer.possibleStartDate;
      }
      this.initialOrder.id = null;
      this.initialOrder.isCurrent = window.isCurrent ?? true;
      this.initialOrder.channel = this.offer.salesChannel;
      this.initialOrder.phone = this.offer.mobile;
      this.initHousing = (this.offer.housing as any) || this.initHousing;
      this.initArea = this.offer.area || this.initArea;
      this.initConsumption = this.offer.consumption || this.initConsumption;

      console.debug("Initial order from offer", this.initialOrder, this.offer);
    } catch (error) {
      console.error(error, "Failed to parse init order");
    }
  }

  // Parse params from price entry on start page
  private parseParams() {
    if (!window.location && !(window as any).serverLocation) {
      return;
    }
    let params = parseQueryString(
      window.location
        ? window.location.search
        : (window as any).serverLocation.search
    );

    let consumption = parseInt(params.get("c") as string);
    if (consumption) {
      this.initConsumption = consumption;
    }
    let housing = params.get("h");
    if (housing == "R") {
      this.initHousing = Housing.RADHUS;
    } else if (housing == "V") {
      this.initHousing = Housing.VILLA;
    }

    let area = parseInt(params.get("a") as string);
    if (area) {
      this.initArea = area;
    }

    this.initialObjectId = params.get("objectId") || "";
    this.initialAccessDate = params.get("accessDate") || "";

    // Parse init order from params
    this.initialOrder.pid = params.get("pid") || "";
    this.initialOrder.firstName = params.get("firstName") || "";
    this.initialOrder.lastName = params.get("lastName") || "";
    this.initialOrder.phone = params.get("phone") || "";
    this.initialOrder.email = params.get("email") || "";
    this.initialOrder.street = params.get("street") || "";
    this.initialOrder.zipCode = params.get("zipCode") || "";
    this.initialOrder.city = params.get("city") || "";
    this.initialOrder.isCurrent =
      params.get("isCurrent") == "0"
        ? false
        : window.isCurrent !== undefined
        ? window.isCurrent
        : true;
  }

  componentDidMount() {
    (window as any).selectAgent && !this.isWarrant && this.fetchAgents();
    this.initialObjectId && this.fetchObject();
  }

  render() {
    return (
      <Formsy
        onSubmit={this.confirm}
        onValid={this.onValidForm}
        onInvalid={this.onInvalidForm}
        mapping={this.mapInputs}
        ref={this.formRef}
        formNoValidate
        noValidate={true}>
        {this.offer && !!this.offer.comparingMonth && (
          <React.Fragment>
            {this.offer.salesChannel == Channel.SWADD ? (
              <p className="copy">
                Vi har jämfört fakturan du skickade in via Swadd:
              </p>
            ) : (
              <p className="copy">
                Vi har gjort en prisjämförelse och här är resultatet:
              </p>
            )}

            <p className="offer-price">Du sparar: {this.offerPrice} kr/år</p>
            <table>
              <tr>
                <th></th>
                <th>Nuvarande leverantör</th>
                <th>Rebel</th>
              </tr>
              <tr>
                <th>Elpris</th>
                <td>{this.offer.variablePriceCompetitor} öre/kWh</td>
                <td>{this.offer.variablePriceRebel} öre/kWh</td>
              </tr>
              <tr>
                <th>Fast avgift</th>
                <td>{this.offer.fixedPriceCompetitor} kr/mån</td>
                <td>{this.offer.fixedPriceRebel} kr/mån</td>
              </tr>
              <tr>
                <th>Årsförbrukning</th>
                <td>{this.offer.consumption} kWh/år</td>
                <td>{this.offer.consumption} kWh/år</td>
              </tr>
              <tr>
                <th>Årskostnad</th>
                <td>{this.offerCompetitorPrice} kr</td>
                <td>{this.offerRebelPrice} kr</td>
              </tr>
            </table>
            <p className="h1 mt-5">Teckna i ett par snabba steg här!</p>
            <p className="copy">
              Du får:
              <br />
              - Garanterat billigare el med prisgaranti
              <br />
              - Ett enkelt rörligt elavtal utan bindningstid
              <br />- Ingen uppsägningstid
              <br />- 100% fossilfritt
              <br />- Busenkla digitala fakturor
            </p>
          </React.Fragment>
        )}
        {(this.offer?.salesChannel == Channel.SODERBERG_PARTNERS ||
          this.offer?.salesChannel == Channel.SODERBERG_PARTNERS_SALARY) && (
          <>
            <p className="copy">
              {this.offer?.salesChannel == Channel.SODERBERG_PARTNERS
                ? "I samband med din pensionsrådgivning så angav du "
                : "Du angav "}
              att du bor i <b>{this.housingToString(this.offer.housing)}</b> på{" "}
              <b>
                {this.offer.area}
                kvm
              </b>{" "}
              och att {this.negotiatePeriodToString(this.offer.negotiatePeriod)}
              .
            </p>
            {this.offer.negotiatePeriod == NegotiatePeriod.ONE_YEAR ? (
              <p className="copy">
                Baserat på att du nyligen bytt elavtal eller elleverantör så
                behöver vi mer information för att kunna avgöra din besparing.
                Hör av dig till oss på{" "}
                <a href="mailto:info@renenkelbilligel.se">
                  info@renenkelbilligel.se
                </a>{" "}
                med en bild eller motsvarande på din senaste elfaktura och
                fakturaspec så svarar vi med hur mycket du sparar på att byta.
                Om du själv gjort en jämförelse kan du gå vidare nedan.
              </p>
            ) : (
              <>
                <p className="copy">
                  Baserat på de uppgifterna har vi värderat din årsförbrukning
                  och uppskattat följade:
                </p>
                <SavingsCalculator
                  offer={this.offer}
                  onChangeConsumption={(consumption: number) =>
                    this.setState({ consumption })
                  }
                />
                <p className="copy">
                  <i>
                    Om du känner till din årsförbrukning eller tycker att
                    värderingen blivit felaktig kan du själv ändra den här
                    ovanför och få en ny beräknad besparing. Har du några
                    funderingar är du alltid välkommen att kontakta oss på{" "}
                    <a href="mailto:info@renenkelbilligel.se">
                      info@renenkelbilligel.se
                    </a>
                  </i>
                </p>
              </>
            )}
            <p className="h1 mt-5">Teckna här så sköter vi allt med bytet!</p>
            <p className="copy">
              Du får:
              <br />
              - Garanterat billigare el med prisgaranti
              <br />
              - Ett enkelt rörligt elavtal utan bindningstid
              <br />- Ingen uppsägningstid
              <br />- 100% fossilfri el
              <br />- Busenkla digitala fakturor
            </p>
          </>
        )}
        {!this.isWarrant &&
          !(this.offer?.comparingMonth > 0) &&
          !(
            this.offer?.salesChannel == Channel.SODERBERG_PARTNERS ||
            this.offer?.salesChannel == Channel.SODERBERG_PARTNERS_SALARY ||
            this.offer?.salesChannel == Channel.SODERBERG_PARTNERS_TM ||
            (window as any).channel == Channel.SODERBERG_PARTNERS_TM
          ) && (
            <>
              <div className="form-group">
                <label className="control-label mb-0">Hur bor du?</label>
                {!this.state.hideCompanyHint && (
                  <p>
                    Har du företag? Maila till{" "}
                    <a href="mailto:info@renenkelbilligel.se">
                      info@renenkelbilligel.se
                    </a>
                  </p>
                )}
                <PriceCalculator
                  onChangeConsumption={this.onChangeConsumption}
                  initArea={this.initArea}
                  initConsumption={this.initConsumption}
                  initHousing={this.initHousing}
                  ref={this.priceCalculatorRef}
                />
              </div>
            </>
          )}
        {(window as any).objectChannel && !this.isWarrant && (
          <>
            <div className="form-group">
              <label className="control-label">Mäklarinformation</label>
              {(window as any).objectChannel == RealEstateChannel.V ? (
                <i>
                  Ange datum för tillträde eller kontraktsskrivning samt ID på
                  kontraktet.
                </i>
              ) : null}
            </div>
            {(window as any).objectChannel == RealEstateChannel.V ? (
              <div className="form-group">
                <DateInput
                  name="objectAccessDate"
                  value={this.initialAccessDate}
                  placeholder={"Inflyttsdatum"}
                  innerRef={this.objectAccessDateRef}
                  skipFirstInput={this.state.skipFirstInput}
                  onValidInput={() => this.fetchObject()}
                />
              </div>
            ) : null}
            <div className="form-group">
              <Input
                name="objectId"
                placeholder={
                  (window as any).objectChannel == RealEstateChannel.V
                    ? "Kontraktets ID"
                    : "Objektets ID"
                }
                value={this.initialObjectId}
                onValidInput={() => this.fetchObject()}
                skipFirstInput={this.state.skipFirstInput}
                ref={this.objectIdRef}
              />
            </div>

            {this.state.fetchingObject && <Spinner />}
            {this.state.fetchObjectNotFound && (
              <p>
                Objektet hittades inte, var vänlig kontrollera ID
                {(window as any).objectChannel == RealEstateChannel.V && (
                  <a href="#" onClick={() => this.fetchObject(true)}>
                    <br />
                    Klicka här för att utöka sökningen med intilligande datum.
                  </a>
                )}
              </p>
            )}

            {this.state.fetchObjectAlreadyCreated && (
              <p className="text-danger">
                Avtalet är redan registrerat, vill du verkligen fortsätta?
              </p>
            )}

            <div className="form-group">
              <Input
                name="governmentApartmentNumber"
                placeholder={"LMV-nummer"}
                value={""}
                ref={this.governmentApartmentNumberRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
            <div className="form-group">
              <Input
                name="apartmentNumber"
                placeholder={"Internt lägenhetsnummer"}
                value={""}
                ref={this.apartmentNumberRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
            {this.state.buyers?.length > 0 ? (
              <div className="form-group">
                <select
                  name="buyers"
                  value={this.state.selectedBuyerId}
                  onChange={this.onChangeBuyer}
                  className={"form-control"}>
                  <option value={"-1"}>Välj köpare</option>
                  {this.state.buyers.map((buyer, index) => (
                    <option value={index} key={index + "-" + buyer.email}>
                      {buyer.firstName} {buyer.lastName},{" "}
                      {buyer.email ? buyer.email : "ingen mail"},{" "}
                      {buyer.mobile ? buyer.mobile : "inget telefonnummer"}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>{this.state.buyers ? <p>Inga köpare hittades</p> : null}</>
            )}
            {this.state.sellers?.length > 0 ? (
              <div className="form-group">
                <select
                  name="sellers"
                  value={this.state.selectedSellerId}
                  onChange={this.onChangeSeller}
                  className={"form-control"}>
                  <option value={"-1"}>Välj säljare</option>
                  {this.state.sellers.map((seller, index) => (
                    <option value={index} key={index + "-" + seller.email}>
                      {seller.firstName} {seller.lastName},{" "}
                      {seller.email ? seller.email : "ingen mail"},{" "}
                      {seller.mobile ? seller.mobile : "inget telefonnummer"}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>{this.state.sellers ? <p>Inga säljare hittades</p> : null}</>
            )}
            {(window as any).selectService ? (
              <>
                <div className="form-group mb-0" id="person">
                  <label className="control-label">Välj tilläggstjänster</label>
                  <MultiselectGroup
                    name="services"
                    buttons={[
                      {
                        label: "El",
                        value: "electricity",
                      },
                      {
                        label: "Försäkring",
                        value: "insurance",
                      },
                    ]}
                    value={""}
                    required></MultiselectGroup>
                </div>
                {this.state.fetchObjectAlreadyCreated && (
                  <p className="text-danger  mb-0">
                    Avtalet är redan registrerat, vill du verkligen fortsätta?
                  </p>
                )}
              </>
            ) : null}
          </>
        )}
        {(window as any).showCustomerType ? (
          <div className="form-group">
            <label className="control-label">Vilken typ av kund?</label>
            <RadioGroup
              name="orderType"
              buttons={[
                {
                  label: "Privat",
                  value: CustomerType[CustomerType.Private],
                },
                {
                  label: "Företag",
                  value: CustomerType[CustomerType.Business],
                },
              ]}
              value={CustomerType[this.state.customerType]}
              onChange={this.onChangeCustomerType}
            />
          </div>
        ) : null}
        <div className="form-group" id="person">
          <label className="control-label">Vem är du?</label>
          <PidInput
            name="pid"
            value={this.initialOrder.pid}
            onValidPid={this.onValidPid}
            disabled={this.state.fetchingAddress}
            innerRef={this.pidRef}
            skipFirstInput={this.state.skipFirstInput}
            customerType={this.state.customerType}
            required={(window as any).requirePid ?? true}
          />
          {this.state.fetchingAddress && <Spinner />}
          {this.state.fetchAddressNotFound && (
            <p>Felaktigt personnummer, var vänlig försök igen</p>
          )}
        </div>
        <div
          className={
            this.state.showNameInputs ||
            this.state.customerType == CustomerType.Business
              ? ""
              : "d-none"
          }>
          <div
            className={
              "form-group" +
              (this.state.customerType == CustomerType.Business
                ? " d-none"
                : "")
            }>
            <Input
              value={this.initialOrder.firstName}
              name="firstName"
              placeholder={"Förnamn"}
              required={this.state.customerType != CustomerType.Business}
              ref={this.firstNameRef}
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
          <div className="form-group">
            <Input
              value={this.initialOrder.lastName}
              name="lastName"
              placeholder={
                this.state.customerType == CustomerType.Business
                  ? "Företagsnamn"
                  : "Efternamn"
              }
              required
              ref={this.lastNameRef}
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="control-label ">
            Vilka är dina kontaktuppgifter?
          </label>
          <PhoneInput
            value={this.initialOrder.phone}
            name="phone"
            validate={!((window as any).skipValidation ?? false)}
            innerRef={this.mobileRef}
            skipFirstInput={this.state.skipFirstInput}
          />
        </div>
        <div className="form-group">
          <EmailInput
            value={this.initialOrder.email}
            name="email"
            validate={!((window as any).skipValidation ?? false)}
            innerRef={this.emailRef}
            skipFirstInput={this.state.skipFirstInput}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Vart ska elen?</label>
          <Input
            name="street"
            placeholder="Gata"
            required
            value={this.initialOrder.street}
            ref={this.streetRef}
            skipFirstInput={this.state.skipFirstInput}
          />
        </div>
        <div className="form-group">
          <Input
            placeholder="Postnummer"
            required
            name="zipCode"
            value={this.initialOrder.zipCode}
            ref={this.zipCodeRef}
            adjustValue={this.adjustZipcode}
            skipFirstInput={this.state.skipFirstInput}
          />
        </div>
        <div className="form-group">
          <Input
            placeholder="Postort"
            required
            name="city"
            value={this.initialOrder.city}
            ref={this.cityRef}
            skipFirstInput={this.state.skipFirstInput}
          />
        </div>
        {!(window as any).objectChannel &&
        this.state.housing == Housing.LAGENHET ? (
          <div className="form-group">
            <Input
              name="governmentApartmentNumber"
              placeholder={"Lägenhetsnummer"}
              value={""}
              ref={this.governmentApartmentNumberRef}
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
        ) : null}
        <div className="form-group">
          <label className="control-label">Bor du där idag?</label>
          <RadioGroup
            name="orderType"
            buttons={[
              {
                label: "Korrekt",
                value: OrderType[OrderType.LEVBYTE],
              },
              {
                label: "Nej, jag ska flytta dit",
                value: OrderType[OrderType.FLYTT],
              },
            ]}
            value={OrderType[this.state.orderType]}
            onChange={this.onChangeOrderType}
          />
        </div>
        {this.state.orderType == OrderType.FLYTT && (
          <React.Fragment>
            <div className="form-group">
              <label className="control-label">
                Vet du vem som bor där idag?
              </label>
              <Input
                value={this.initialOrder.currentFirstName ?? ""}
                name="currentFirstName"
                placeholder={"Förnamn"}
                required
                ref={this.currentFirstNameRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
            <div className="form-group">
              <Input
                value={this.initialOrder.currentLastName ?? ""}
                name="currentLastName"
                placeholder={"Efternamn"}
                required
                ref={this.currentLastNameRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
          </React.Fragment>
        )}
        {this.state.orderType != OrderType.LEVBYTE && (
          <div className="form-group">
            <label className="control-label">
              Vilket datum flyttar ni in, aka tillträdesdatum?
            </label>
            <DateInput
              name="startDate"
              placeholder="åååå-mm-dd"
              required
              value={
                this.initialOrder.startDate
                  ? this.initialOrder.startDate.format("YYYY-MM-DD")
                  : ""
              }
              min={moment().format("YYYY-MM-DD")}
              innerRef={this.startDateRef}
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
        )}
        {this.state.orderType == OrderType.LEVBYTE &&
          (window as any).showStartDate && (
            <div className="form-group">
              <label className="control-label">
                Från vilket datum ska vi ta över elen (valfritt)?
              </label>
              <DateInput
                name="startDate"
                placeholder="åååå-mm-dd"
                value={
                  this.initialOrder.startDate
                    ? this.initialOrder.startDate.format("YYYY-MM-DD")
                    : ""
                }
                min={moment().format("YYYY-MM-DD")}
                innerRef={this.startDateRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
          )}
        {(window as any).channel == Channel.FRIBERGASKOLAN &&
          !this.isWarrant && (
            <div className="form-group">
              <label className="control-label">
                Vilken elev på Fribergaskolan vill du stötta?
              </label>
              <Input
                value={""}
                name="salesperson"
                placeholder={"Förnamn Efternamn"}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
          )}
        {(window as any).selectAgent == true && !this.isWarrant && (
          <div className="form-group">
            <label className="control-label">Vilken mäklare?</label>
            {this.state.AgentStatus == FetchStatus.DONE && (
              <Typeahead
                id="RealEstateAgent"
                ref={this.AgentRef}
                labelKey={(agent: RealEstateAgentModel) =>
                  agent.name +
                  " - " +
                  agent.offices?.map((office) => office.location).join(", ")
                }
                multiple={false}
                options={this.state.Agents}
                placeholder="Välj mäklare..."
                onChange={this.setAgent}
                onBlur={this.blurAgent}
                selected={this.state.Agent ? [this.state.Agent] : []}
                //defaultSelected={this.state.Agent ? [this.state.Agent] : []}
                paginationText={"Visa fler..."}
                selectHintOnEnter={true}
              />
            )}
            {this.state.AgentStatus == FetchStatus.FETCHING && <Spinner />}
            {this.state.AgentStatus == FetchStatus.ERROR && (
              <p className="danger">Kunde inte hämta mäklare</p>
            )}

            <div className="form-group">
              <label className="control-label">
                Säljarens kontaktuppgifter
              </label>
              <RadioGroup
                name="orderType"
                buttons={[
                  {
                    label: "Privat",
                    value: CustomerType[CustomerType.Private],
                  },
                  {
                    label: "Företag",
                    value: CustomerType[CustomerType.Business],
                  },
                ]}
                value={CustomerType[this.state.sellerCustomerType]}
                onChange={this.onChangeSellerCustomerType}
              />
            </div>
            <div className="form-group">
              <PidInput
                value=""
                name="contactPid"
                onValidPid={this.onValidContactPid}
                innerRef={this.contactPidRef}
                skipFirstInput={this.state.skipFirstInput}
                customerType={
                  this.state.sellerCustomerType ?? CustomerType.Private
                }
                required={(window as any).requirePid ?? true}
              />
            </div>
            {this.state.fetchingContact && (
              <div className="mt-1">
                <Spinner />
              </div>
            )}
            <div
              className={
                "form-group" +
                (this.state.sellerCustomerType == CustomerType.Business
                  ? " d-none"
                  : "")
              }>
              <Input
                value=""
                name="contactFirstName"
                placeholder="Förnamn"
                ref={this.contactFirstNameRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
            <div className="form-group">
              <Input
                value=""
                name="contactLastName"
                placeholder={
                  this.state.sellerCustomerType == CustomerType.Business
                    ? "Företagsnamn"
                    : "Efternamn"
                }
                ref={this.contactLastNameRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
            <div className="form-group">
              <Input
                value=""
                placeholder="Mobilnummer"
                type="tel"
                name="contactPhone"
                validations={{
                  matchRegexp: /^((07){1}\d{8})$/,
                }}
                validationError="Använd 10 siffror för mobilnummer"
                ref={this.contactPhoneRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
            <div className="form-group">
              <Input
                value=""
                placeholder="Epostadress"
                validations="isEmail"
                type="email"
                name="contactEmail"
                validationError="Prova igen, skriv in en epostadress"
                ref={this.contactEmailRef}
                skipFirstInput={this.state.skipFirstInput}
              />
            </div>
          </div>
        )}
        {(window as any).showEan == true && !this.isWarrant && (
          <div className="form-group">
            <label className="control-label">
              Vilket EAN har anläggningen (valfritt)?
            </label>
            <EanInput name="ean" value={this.initialOrder.ean ?? ""} />
          </div>
        )}
        {(window as any).channel == "NykvarnEl" && !this.isWarrant && (
          <NykvarnTeamSelection
            initClub={this.initialOrder.club}
            initTeam={this.initialOrder.team}
            onChange={this.onChangeTeamSelection}
          />
        )}
        {(window as any).showSalesData1 == true && (
          <div className="form-group">
            {!!window.ClientCopyOverride?.SignupSalesDate1Label && (
              <label className="control-label">
                {window.ClientCopyOverride?.SignupSalesDate1Label}
              </label>
            )}
            <Input
              name="salesData1"
              value={""}
              placeholder={
                window.ClientCopyOverride?.SignupSalesDate1Placeholder ?? ""
              }
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
        )}
        {(window as any).showSalesData2 == true && (
          <div className="form-group">
            {!!window.ClientCopyOverride?.SignupSalesDate2Label && (
              <label className="control-label">
                {window.ClientCopyOverride?.SignupSalesDate2Label}
              </label>
            )}
            <Input
              name="salesData2"
              value={""}
              placeholder={
                window.ClientCopyOverride?.SignupSalesDate2Placeholder ?? ""
              }
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
        )}
        {(window as any).showSalesDate == true && (
          <div className="form-group">
            <label className="control-label">{"Försäljningsdatum"}</label>
            <DateInput
              name="salesDate"
              value={""}
              required
              placeholder={"Försäljningsdatum"}
              innerRef={this.salesDateRef}
              skipFirstInput={this.state.skipFirstInput}
            />
          </div>
        )}

        {(window as any).updateOffer == true ? null : (
          <div className="form-group">
            <label className="control-label">Godkänn och skicka in</label>
            <div className="frb-group">
              <Checkbox
                name="terms"
                value={this.isWarrant}
                required="isFalse"
                position={"left"}
                labelBold={"Jag godkänner"}
                label={
                  this.state.orderType == OrderType.LEVBYTE
                    ? window.ClientCopyOverride?.SignupTermsChange ??
                      " Rebels villkor och ger dem fullmakt att hämta mina uppgifter kring elen och säga upp mitt nuvarande elavtal så att de kan ordna mitt byte snarast möjligt."
                    : window.ClientCopyOverride?.SignupTermsMove ??
                      " Rebels villkor och ger dem fullmakt att hantera min inflytt med nätbolaget samt hämta de uppgifter som krävs för att genomföra mitt byte till Rebel."
                }
              />
            </div>
            <div className="col text-center">
              <a href="/villkor" target="_blank">
                {window.ClientCopyOverride?.SignupTermsLink ??
                  "Läsa mer om Rebels villkor här"}
              </a>
            </div>
          </div>
        )}
        {this.isWarrant ? (
          <React.Fragment>
            <p className="control-label mb-0">
              IP-Nummer: {this.initialOrder.ip}
            </p>
            {this.initialOrder.createdOn && (
              <p className="control-label mt-0 mb-0">
                Försäljningsdatum:{" "}
                {this.initialOrder.createdOn.format("YYYY-MM-DD")}
              </p>
            )}
            <p className="control-label mt-0">
              Fullmakten är giltig tills bytet är klart eller i max 6 månader
            </p>
          </React.Fragment>
        ) : (
          <div className="form-group">
            <button
              className="btn btn-submit btn-lg"
              type="submit"
              disabled={
                !this.state.valid || this.state.status == Status.POSTING
              }>
              {(window as any).updateOffer == true
                ? this.offer == null
                  ? "Skicka till kund"
                  : "Uppdatera och skicka"
                : "Skicka beställning"}
            </button>
            <div className="text-center mt-2">
              <a href="#howItWorksModal" data-toggle="modal">
                Hur fungerar det?
              </a>
            </div>
          </div>
        )}
        <HowItWorksModal />
        <ConfirmModal
          ref={this.confirmRef}
          onConfirm={this.submit}
          onClose={this.cancelSubmit}
          status={this.state.status}
          show={this.state.status != Status.DRAFT}
          requireSignature={(window as any).requireSignature == true}
        />
      </Formsy>
    );
  }

  housingToString = (housing: Housing) => {
    switch (housing) {
      case Housing.FRITIDSHUS:
        return "ett fritidshus";
      case Housing.LAGENHET:
        return "en lägenhet";
      case Housing.RADHUS:
        return "ett radhus";
      case Housing.VILLA:
        return "en villa";
      default:
        "";
    }
  };

  negotiatePeriodToString = (negotiatePeriod: NegotiatePeriod) => {
    switch (negotiatePeriod) {
      case NegotiatePeriod.ONE_YEAR:
        return "du bytt elavtal under de senaste året";
      case NegotiatePeriod.FIVE_YEARS:
        return "senast du bytte elavtal var för 3-5 år sedan";
      case NegotiatePeriod.THREE_YEARS:
        return "senast du bytte elavtal var för 1-3 år sedan";
      case NegotiatePeriod.NEVER:
        return "du inte gjort något aktivt val av elavtal";
      default:
        return "";
    }
  };

  adjustZipcode = (zipcode: string): string => {
    // replace all non digits
    zipcode = zipcode.replace(/[^\d]/g, "");
    return zipcode;
  };

  fetchAgents = async () => {
    try {
      this.setState({ AgentStatus: FetchStatus.FETCHING });
      let Agents = await new RealEstateAgentClient().list(
        undefined,
        (window as any).channel,
        undefined
      );
      this.setState({ Agents, AgentStatus: FetchStatus.DONE });
    } catch (error) {
      console.error("Failed to fetch Agents", error);
      this.setState({ AgentStatus: FetchStatus.ERROR });
    }
  };

  setAgent = (Agent: RealEstateAgentModel[]) => {
    this.setState({ Agent: Agent.length > 0 ? Agent[0] : null });
  };

  blurAgent = () => {
    if (!this.state.Agent) {
      this.AgentRef.current && this.AgentRef.current.clear();
    }
  };

  onValidPid = async (pid: string) => {
    if (this.state.customerType != CustomerType.Business) {
      try {
        this.setState({
          fetchingAddress: true,
          fetchAddressNotFound: false,
          showNameInputs: false,
        });
        this.firstNameRef.current.setValue("");
        this.lastNameRef.current.setValue("");
        let address = await this.addressClient.getByPid(pid);

        this.firstNameRef.current.setValue(address.firstName);
        this.lastNameRef.current.setValue(address.lastName);
        if (this.state.orderType != OrderType.FLYTT) {
          !this.streetRef.current.getValue() &&
            this.streetRef.current.setValue(address.street);
          !this.zipCodeRef.current.getValue() &&
            this.zipCodeRef.current.setValue(address.zipCode);
          !this.cityRef.current.getValue() &&
            this.cityRef.current.setValue(address.city);
        }

        this.setState({ fetchingAddress: false, showNameInputs: true });
      } catch (error) {
        if (error.status == 404) {
          this.setState({
            fetchingAddress: false,
            showNameInputs: false,
            fetchAddressNotFound: true,
          });
        } else {
          this.setState({ fetchingAddress: false, showNameInputs: true });
        }
      }
    }
  };

  onChangeBuyer = (event: ChangeEvent<HTMLSelectElement>) => {
    this.setState({ selectedBuyerId: event.target.value });
    this.updateBuyer(
      event.target.value == "-1" ? null : this.state.buyers[event.target.value]
    );
  };

  onChangeSeller = (event: ChangeEvent<HTMLSelectElement>) => {
    this.setState({ selectedSellerId: event.target.value });
    this.updateSeller(
      event.target.value == "-1" ? null : this.state.sellers[event.target.value]
    );
  };

  updateBuyer = (buyer: RealEstateObjectConsumers) => {
    if (buyer?.pid?.length == 10 || buyer?.pid?.length == 12) {
      this.pidRef.current?.setValue(buyer?.pid ?? "");
    }
    this.firstNameRef.current?.setValue(buyer?.firstName ?? "");
    this.lastNameRef.current?.setValue(buyer?.lastName ?? "");
    this.mobileRef.current?.setValue(buyer?.mobile ?? "");
    this.emailRef.current?.setValue(buyer?.email ?? "");
    this.setState({
      customerType: buyer?.customerType ?? CustomerType.Private,
    });
  };

  updateSeller = (seller: RealEstateObjectConsumers) => {
    this.contactPidRef.current?.setValue(seller?.pid ?? "");
    this.contactPidRef.current?.validate?.(seller?.pid ?? "");
    this.contactFirstNameRef.current?.setValue(seller?.firstName ?? "");
    this.contactLastNameRef.current?.setValue(seller?.lastName ?? "");
    this.contactPhoneRef.current?.setValue(seller?.mobile ?? "");
    this.contactEmailRef.current?.setValue(seller?.email ?? "");

    this.currentFirstNameRef.current?.setValue(seller?.firstName ?? "");
    this.currentLastNameRef.current?.setValue(seller?.lastName ?? "");

    this.setState({
      sellerCustomerType: seller?.customerType ?? CustomerType.Private,
    });
  };

  fetchObject = async (expandDateSearch = false) => {
    try {
      if (this.controller) {
        this.controller.abort();
      }

      const objectId = this.objectIdRef.current?.getValue();
      if (!objectId) {
        return;
      }
      this.controller = new AbortController();

      let object: RealEstateObject;
      if ((window as any).objectChannel == RealEstateChannel.V) {
        const accessDateString = this.objectAccessDateRef.current?.getValue();
        if (!accessDateString) {
          return;
        }
        const accessDate = moment.utc(
          this.objectAccessDateRef.current?.getValue(),
          "YYYY-MM-DD"
        );
        if (!accessDate.isValid()) {
          throw new Error("Invalid accessDate");
        }
        this.setState({
          fetchingObject: true,
          fetchObjectNotFound: false,
          fetchObjectAlreadyCreated: false,
          orderType: OrderType.FLYTT,
        });
        object = await this.objectClient.get(
          objectId,
          accessDate,
          (window as any).objectCustomerId,
          RealEstateChannel.V,
          (window as any).channel,
          expandDateSearch,
          this.controller.signal
        );
      } else {
        this.setState({
          fetchingObject: true,
          fetchObjectNotFound: false,
          fetchObjectAlreadyCreated: false,
          orderType: OrderType.FLYTT,
        });
        object = await this.objectClient.get(
          objectId,
          undefined,
          undefined,
          (window as any).objectChannel,
          (window as any).channel,
          expandDateSearch,
          this.controller.signal
        );
      }

      console.debug("Got real estate object", object);

      this.updateBuyer(object.buyers?.length == 1 ? object.buyers[0] : null);
      this.setState({
        selectedBuyerId: object.buyers?.length == 1 ? "0" : "-1",
      });

      this.streetRef.current?.setValue(object.street);
      this.zipCodeRef.current?.setValue(object.zipCode);
      this.cityRef.current?.setValue(object.city);

      this.updateSeller(object.sellers?.length == 1 ? object.sellers[0] : null);
      this.setState({
        selectedSellerId: object.sellers?.length == 1 ? "0" : "-1",
      });

      this.startDateRef.current?.setValue(
        object.startDate?.format("YYYY-MM-DD") ?? ""
      );

      this.salesDateRef.current?.setValue(
        object.salesDate?.format("YYYY-MM-DD") ?? ""
      );

      this.governmentApartmentNumberRef.current?.setValue(
        object.lmvNumber ?? ""
      );
      this.apartmentNumberRef.current?.setValue(
        object.internalPropertyNumber ?? ""
      );

      this.priceCalculatorRef.current?.onChangeHousing(object.housing);
      this.priceCalculatorRef.current?.setArea(object.area);

      if (object.realEstateAgent) {
        const agent = this.state.Agents?.find(
          (agent) => agent.email == object.realEstateAgent.email
        );
        if (agent == null) {
          this.setState({
            Agents: (this.state.Agents ?? []).concat(object.realEstateAgent),
          });
        }
        this.setState({ Agent: object.realEstateAgent });
      } else {
        this.setState({ Agent: null });
      }

      this.setState({
        fetchingObject: false,
        fetchObjectNotFound: false,
        fetchObjectAlreadyCreated: object.orderCreated,
        showNameInputs: true,
        buyers: object.buyers,
        sellers: object.sellers,
        object: object,
        objectStatus: object.status,
        objectStatusText: object.statusText,
        skipFirstInput: true,
      });
    } catch (error) {
      if (error?.name == "AbortError") {
        console.debug("Object fetch aborted");
      } else {
        console.debug("Failed to update object information", { error });
        this.setState({
          fetchingObject: false,
          fetchObjectNotFound: true,
          fetchObjectAlreadyCreated: false,
        });
      }
    }
  };

  onValidContactPid = async (pid: string) => {
    try {
      this.setState({ fetchingContact: true });
      let response = await this.addressClient.getByPid(pid);
      this.contactFirstNameRef.current.setValue(response.firstName);
      this.contactLastNameRef.current.setValue(response.lastName);
    } catch (error) {
      console.error("Failed to fetch contact name", error);
    } finally {
      this.setState({ fetchingContact: false });
    }
  };

  onChangeOrderType = (button: Item) => {
    let orderType: OrderType = OrderType[button.value];
    this.setState({ orderType });
  };

  onChangeCustomerType = (button: Item) => {
    let customerType: CustomerType = CustomerType[button.value];
    this.setState({ customerType });
  };

  onChangeSellerCustomerType = (button: Item) => {
    let sellerCustomerType: CustomerType = CustomerType[button.value];
    this.setState({ sellerCustomerType });
  };

  onChangeConsumption = (consumption: number, housing: Housing): void => {
    this.setState({
      consumption,
      housing,
    });
  };

  onChangeTeamSelection = (club: string, team: string) => {
    this.setState({ team, club });
  };

  mapContactInputs = (inputs: any): Contact => {
    if (!inputs.contactLastName) {
      return null;
    }

    let phone = inputs.contactPhone;
    if (phone?.startsWith("07") && phone?.length == 10) {
      phone = "+46" + phone.substring(1);
    }

    return {
      pid: inputs.contactPid,
      firstName: inputs.contactFirstName,
      lastName: inputs.contactLastName,
      mobile: phone,
      email: inputs.contactEmail,
      contactChannel: ContactChannel.SMS,
      contactType: ContactType.REALESTATESELLER,
    } as Contact;
  };

  mapInputs = (inputs: any): Order => {
    let priceState =
      this.priceCalculatorRef.current &&
      this.priceCalculatorRef.current.getState();

    let phone = inputs.phone;
    if (phone?.startsWith("07") && phone?.length == 10) {
      phone = "+46" + phone.substring(1);
    }
    return {
      pid: inputs.pid,
      firstName: inputs.firstName,
      lastName: inputs.lastName,
      phone: phone,
      email: inputs.email,
      street: inputs.street,
      zipCode: inputs.zipCode,
      city: inputs.city,
      currentFirstName: inputs.currentFirstName,
      currentLastName: inputs.currentLastName,
      startDate: inputs.startDate,
      salesperson: inputs.salesperson,
      club: this.state.club,
      team: this.state.team,
      realEstateAgentId: this.state.Agent?.id,
      realEstateOfficeId: this.state.object?.realEstateOffice?.id,
      realEstateChainId: this.state.object?.realEstateChain?.id,
      ean: inputs.ean || this.initialOrder.ean,
      contact: this.mapContactInputs(inputs),
      salesData1: inputs.salesData1,
      salesData2: inputs.salesData2,
      salesData3: inputs.salesData3,
      salesData4: inputs.salesData4,
      salesDate: inputs.salesDate,
      objectId: this.state.object?.id,
      objectCustomerId: (window as any).objectCustomerId,
      objectChannel: (window as any).objectChannel,
      objectStatus: this.state.objectStatus,
      objectStatusText: this.state.objectStatusText,
      lmvNumber: inputs.governmentApartmentNumber,
      internalPropertyNumber: inputs.apartmentNumber,
      isCurrent: this.state.orderType == OrderType.LEVBYTE,
      channel:
        this.initialOrder.channel || (window as any).channel || Channel.WEB,
      offerId: this.offer ? this.offer.id : null,
      area: priceState ? priceState.area : this.initArea,
      consumption:
        priceState?.consumption ??
        this.state.consumption ??
        this.initialOrder.consumption,
      housing: priceState ? priceState.housing : this.initHousing,
      hasElectricity:
        inputs.services == null
          ? true
          : inputs.services
              .split(",")
              .findIndex((service) => service == "electricity") != -1,
      hasInsurance:
        inputs.services == null
          ? false
          : inputs.services
              .split(",")
              .findIndex((service) => service == "insurance") != -1,
      customerType: this.state.customerType ?? CustomerType.Private,
    } as Order;
  };

  confirm = async (order: Order) => {
    if (this.posting || !this.state.valid) {
      return;
    }

    this.confirmRef.current.show(order);
    this.posting = true;
    this.setState({
      status: Status.CONFIRM,
    });
  };

  submit = async (signatureId?: string) => {
    this.setState({
      status: Status.POSTING,
    });
    let order = this.formRef.current.getModel() as Order;
    order.isCurrent = this.state.orderType == OrderType.LEVBYTE;
    order.channel =
      this.initialOrder.channel || (window as any).channel || Channel.WEB;
    order.offerId = this.offer ? this.offer.id : null;

    let priceState =
      this.priceCalculatorRef.current &&
      this.priceCalculatorRef.current.getState();
    order.area = priceState ? priceState.area : this.initArea;
    order.consumption =
      priceState?.consumption ??
      this.state.consumption ??
      this.initialOrder.consumption;
    order.housing = priceState ? priceState.housing : this.initHousing;
    order.signatureId = signatureId;
    if (!order.startDate) {
      if (this.offer?.possibleStartDate) {
        order.startDate = this.offer.possibleStartDate;
      } else {
        order.startDate = undefined;
      }
    }

    console.debug("submitting order", order);

    try {
      if ((window as any).updateOffer) {
        if (this.offer) {
          await new SoderbergPartnersClient().updateAndSend(
            order.offerId,
            order
          );
        } else {
          await new SoderbergPartnersClient().createAndSend(order);
        }
      } else {
        await new OrderClient().create((window as any).preorder == true, order);
      }
      window.location.replace("/tack");
    } catch (error) {
      console.debug("Order save error", error);
      this.setState({ status: Status.ERROR });
    } finally {
      this.posting = false;
    }
  };

  cancelSubmit = () => {
    this.posting = false;
    this.setState({
      status: Status.DRAFT,
    });
  };

  onValidForm = () => {
    this.setState({
      valid: true,
    });
  };

  onInvalidForm = () => {
    this.setState({
      valid: false,
    });
  };
}
